import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { PropsWithChildren,startTransition  } from "react"
import { useNavigate } from "react-router"
import { cn } from "src/common/utils"

const anchorVariants = cva("underline-offset-1 cursor-pointer", {
  variants: {
    variant: {
      primary: "hover:underline",
      secondary: "underline text-primary hover:text-[#ce5e90]",
      icon: "text-muted-foreground hover:text-foreground transition duration-200",
      noHover: "no-underline hover:no-underline",
    },
    noUnderline: {
      true: "no-underline hover:no-underline",
    }
  },
  defaultVariants: {
    variant: "primary",
    noUnderline: false,
  },
})

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof anchorVariants> {
  asChild?: boolean
  to?: string
  external?: boolean
  noUnderline?: boolean
}

const Link = React.forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(
  ({ className, variant, noUnderline, asChild = false, to, onClick, external, ...props }, ref) => {
    const navigate = useNavigate()
    const Comp = asChild ? Slot : "a"

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Allow meta/ctrl-click to open in new tab
      if (e.metaKey || e.ctrlKey) {
        onClick?.(e)
        return
      }

      if (to && !to.startsWith("http") && !external) {
        e.preventDefault()
        startTransition(() => {
          navigate(to)
        })
      }
      onClick?.(e)
    }

    const externalProps = external ? {
      target: "_blank",
      rel: "noopener noreferrer"
    } : {}

    return (
      <Comp 
        className={cn(anchorVariants({ variant, noUnderline, className }))} 
        onClick={handleClick}
        href={to || props.href}
        ref={ref}
        {...externalProps}
        {...props} 
      />
    )
  },
)
Link.displayName = "Link"

export { Link }
